// Settings card (admin only, Session ASQ, Stream G, Item 5): the privacy notice.
//
// THE RULE: until the notice is saved here, the public /privacy page and every link to it (the customer portal, the
// subcontractor onboarding page, the quote and renewal pages, the NPS survey and every branded email) stay hidden.
// Nothing ever shows a default or placeholder notice.
//
// WHAT CLEARING DOES, SAID HONESTLY (Session ASQ review, F1). Clearing removes the page at once, and every page and every
// email composed AFTER it carries no link. But an email's link is written in when the email or Gmail draft is created,
// and the office sends drafts from Gmail itself, so an email already sent, or a draft already waiting in Gmail, keeps the
// link it was written with (which then leads to the not-found page). The card says exactly that, never "every link".
//
// The preview uses the SAME renderer as the public page (src/components/SimpleMarkdown.tsx), so what the admin checks
// here is what a customer reads there. Saving publishes and Clearing unpublishes, each a two-step confirm (the platform's
// branded confirm for consequential actions). Every change is recorded in the audit log with the full wording.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { PrivacyNoticeSettings } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { SimpleMarkdown } from "../../components/SimpleMarkdown";
import { formatNoticeDate, PRIVACY_NOTICE_LINKED_FROM } from "../../lib/privacyNotice";
import { Loader } from "../../components/Loader";

// What Clearing does (see the header). Exported so the test pins the exact words.
export const PRIVACY_NOTICE_CLEAR_EFFECT =
  "Clearing it removes the page at once, and new pages and new emails stop showing the link. Emails already sent, and drafts already waiting in Gmail, keep the link they were written with, which then leads to a page that is no longer there.";
export const PRIVACY_NOTICE_CLEARED_MESSAGE =
  "Cleared. The privacy notice page is hidden, and new pages and new emails no longer show the link. Emails already sent, and drafts already waiting in Gmail, keep the link they were written with.";
export const PRIVACY_NOTICE_UNPUBLISHED_STATUS = "Not published: the privacy notice page is hidden, and no page or new email shows a link to it.";

// The server's limit (lib/privacyNotice.ts PRIVACY_NOTICE_MAX_CHARS, and a database CHECK). The server is the authority.
const MAX_CHARS = 30_000;

const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function PrivacyNoticeSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [saved, setSaved] = useState<PrivacyNoticeSettings | null>(null);
  const [draft, setDraft] = useState("");
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState<"save" | "clear" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    // Through a resolved promise, so even a synchronous failure lands in the catch as "could not load".
    Promise.resolve()
      .then(() => api.settings.privacyNotice.get())
      .then((s) => {
        if (!live) return;
        setSaved(s);
        setDraft(s.text ?? "");
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  const published = !!saved?.text;
  const trimmed = draft.trim();
  const changed = trimmed !== (saved?.text ?? "").trim();
  const tooLong = trimmed.length > MAX_CHARS;
  const lastChanged = formatNoticeDate(saved?.updatedAt);

  async function write(text: string | null, which: "save" | "clear") {
    setBusy(which);
    setError(null);
    setMessage(null);
    try {
      const next = await api.settings.privacyNotice.put(text);
      setSaved(next);
      setDraft(next.text ?? "");
      setMessage(
        next.text
          ? "Saved. The privacy notice is published and linked from every place listed above."
          : PRIVACY_NOTICE_CLEARED_MESSAGE,
      );
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the privacy notice. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Privacy notice">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Privacy notice</h2>
      <p className="mb-2 text-sm text-ele-body">
        Once saved, the notice is published at <span className="font-menu text-ele-strong">/privacy</span> and linked from {PRIVACY_NOTICE_LINKED_FROM}.
        Until it is saved the page and every link stay hidden. {PRIVACY_NOTICE_CLEAR_EFFECT}
      </p>
      <p className="mb-3 text-sm text-ele-body">
        Paste the approved wording. Start a line with # or ## for a heading, with - or 1. for a list item, put **two
        stars** around words for bold, and a web address starting https:// becomes a link. Lines run together into one
        paragraph: leave a blank line to start a new one. Anything else shows exactly as typed.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !saved ? (
        <p role="alert" className="text-sm text-ele-strong">Could not load the privacy notice.</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ele-strong" data-testid="privacy-notice-status">
            {published ? "Published." : PRIVACY_NOTICE_UNPUBLISHED_STATUS}
            {lastChanged ? ` Last changed ${lastChanged}.` : ""}
          </p>
          <label className="block">
            <span className={labelCls}>Notice wording</span>
            <textarea
              aria-label="Privacy notice wording"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setMessage(null);
              }}
              rows={14}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-mono text-sm text-ele-strong outline-none focus:border-ele-green"
            />
            <span className={`mt-1 block text-xs ${tooLong ? "text-ele-danger" : "text-ele-body"}`}>
              {trimmed.length.toLocaleString("en-GB")} of {MAX_CHARS.toLocaleString("en-GB")} characters
            </span>
          </label>
          <div>
            <span className={labelCls}>Preview</span>
            <div
              className="max-h-96 overflow-y-auto rounded-ele border border-ele-purple-accent bg-ele-field px-4 py-2 text-sm text-ele-strong"
              aria-label="Privacy notice preview"
            >
              {trimmed ? (
                <SimpleMarkdown text={draft} headingLevel={3} classes={{ link: "underline break-all text-ele-blue" }} />
              ) : (
                <p className="my-2 text-ele-body">Nothing to preview yet.</p>
              )}
            </div>
          </div>
          {error && (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          )}
          {message && (
            <p role="status" className="text-sm text-ele-strong">
              {message}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <ConfirmButton
              label={published ? "Save changes" : "Save and publish"}
              confirmLabel={published ? "Publish these changes?" : "Publish on every page and email?"}
              busy={busy === "save"}
              busyLabel="Saving..."
              disabled={!trimmed || !changed || tooLong || busy !== null}
              onConfirm={() => void write(trimmed, "save")}
              className="rounded-ele bg-ele-green px-4 py-2 font-heading text-xs tracking-wide text-ele-bg disabled:opacity-50"
              armedClassName="ring-2 ring-ele-green ring-offset-2 ring-offset-ele-surface"
            />
            {published && (
              <ConfirmButton
                label="Clear notice"
                confirmLabel="Clear the notice and hide the page?"
                busy={busy === "clear"}
                busyLabel="Clearing..."
                disabled={busy !== null}
                onConfirm={() => void write(null, "clear")}
                className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
              />
            )}
            {changed && published && (
              <button
                type="button"
                onClick={() => {
                  setDraft(saved.text ?? "");
                  setError(null);
                }}
                disabled={busy !== null}
                className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
              >
                Undo my edits
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
